// =====================================================================
// Assessment Center — shared UI primitives.
//
// This is the ONE canonical copy. Earlier phases each kept an
// independent copy (instructor/components/ui.tsx, student/components/
// ui.tsx) so neither panel's build depended on the other while they
// were built separately — final integration merged them into this
// single src/shared/components/ui.tsx, taking the superset of both
// (this file had a couple of components, like ConfirmDialog, that the
// student copy didn't need yet; the student copy had a couple of
// status-tone cases — submitted/in_progress/graded — this one didn't
// need yet). Every feature module imports from here.
//
// Kept dependency-free (no headless-ui / radix). Tokens follow
// tailwind.config.js at the project root — do not hardcode other
// colors.
// =====================================================================
import React, { useEffect, useRef } from "react";

// ---------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------
type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

export function Button({
  variant = "primary",
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium " +
    "font-body transition-colors focus:outline-none focus:ring-2 focus:ring-navy-300 " +
    "disabled:opacity-50 disabled:cursor-not-allowed";
  const variants: Record<ButtonVariant, string> = {
    primary: "bg-navy-700 text-white hover:bg-navy-800",
    secondary:
      "bg-white text-navy-700 border border-border-soft hover:bg-navy-50",
    ghost: "text-navy-700 hover:bg-navy-50",
    danger: "bg-white text-red-600 border border-red-200 hover:bg-red-50",
  };
  return <button className={`${base} ${variants[variant]} ${className}`} {...props} />;
}

// ---------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------
export function Card({
  className = "",
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`bg-white border border-border-soft rounded-card shadow-soft ${className}`}
      {...props}
    />
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border-soft px-5 py-4">
      <div>
        <h3 className="font-display text-lg font-semibold tracking-tight text-navy-900">
          {title}
        </h3>
        {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// ---------------------------------------------------------------------
// Badge — status pills, restrained tints only
// ---------------------------------------------------------------------
type BadgeTone = "neutral" | "navy" | "green" | "amber" | "red";

const badgeTones: Record<BadgeTone, string> = {
  neutral: "bg-slate-100 text-slate-600",
  navy: "bg-navy-100 text-navy-700",
  green: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-700",
  red: "bg-red-50 text-red-600",
};

export function Badge({
  tone = "neutral",
  children,
}: {
  tone?: BadgeTone;
  children: React.ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium font-body ${badgeTones[tone]}`}
    >
      {children}
    </span>
  );
}

/** Maps schema status enums to a consistent badge tone across the app. */
export function statusTone(status: string): BadgeTone {
  switch (status) {
    case "published":
    case "active":
    case "submitted":
      return "green";
    case "draft":
    case "not_started":
    case "pending":
      return "neutral";
    case "in_progress":
      return "amber";
    case "closed":
    case "expired":
      return "amber";
    case "graded":
    case "archived":
      return "navy";
    default:
      return "neutral";
  }
}

// ---------------------------------------------------------------------
// Form controls
// ---------------------------------------------------------------------
const controlBase =
  "w-full rounded-lg border border-border-soft bg-white px-3 py-2 text-sm font-body text-slate-800 " +
  "placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-300";

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-navy-900">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input className={controlBase} {...props} />;
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`${controlBase} min-h-[100px] resize-y`} {...props} />;
}

export function Select({
  className = "",
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={`${controlBase} ${className}`} {...props} />;
}

// ---------------------------------------------------------------------
// Modal — simple, accessible, no external deps
// ---------------------------------------------------------------------
export function Modal({
  open,
  onClose,
  title,
  children,
  widthClass = "max-w-lg",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  widthClass?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/40 px-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`w-full ${widthClass} max-h-[85vh] overflow-y-auto rounded-card border border-border-soft bg-white shadow-soft`}
      >
        <div className="flex items-center justify-between border-b border-border-soft px-5 py-4">
          <h3 className="font-display text-lg font-semibold text-navy-900">{title}</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-slate-400 hover:bg-navy-50 hover:text-navy-700"
          >
            ✕
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Empty state — "an empty screen is an invitation to act"
// ---------------------------------------------------------------------
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-card border border-dashed border-border-soft bg-navy-50/40 px-6 py-14 text-center">
      <h3 className="font-display text-lg font-semibold text-navy-900">{title}</h3>
      <p className="mt-1 max-w-sm text-sm text-slate-500">{description}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------
// Confirm dialog — used for delete question / discard, etc.
// ---------------------------------------------------------------------
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Delete",
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal open={open} onClose={onCancel} title={title} widthClass="max-w-sm">
      <p className="text-sm text-slate-600">{description}</p>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------
// Page header — consistent title bar across all instructor pages
// ---------------------------------------------------------------------
export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-navy-900">
          {title}
        </h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Spinner({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-block h-4 w-4 animate-spin rounded-full border-2 border-navy-200 border-t-navy-700 ${className}`}
      aria-label="Loading"
    />
  );
}
